import { useCallback, useEffect, useRef, useState } from "react";
import { Menu } from "lucide-react";
import { useIsMobile } from "./lib/useIsMobile";
import { ChannelSwitcher } from "./components/ChannelSwitcher";
import { MAX_VISIBLE_TOASTS, RenderReadyToast, type RenderToast } from "./components/RenderReadyToast";
import { Sidebar } from "./components/Sidebar";
import { Backlog } from "./screens/Backlog";
import { Calendar } from "./screens/Calendar";
import { Cost } from "./screens/Cost";
import { Guide } from "./screens/Guide";
import { JobDetail } from "./screens/JobDetail";
import { Library } from "./screens/Library";
import { Inspiration } from "./screens/Inspiration";
import { LibraryPosts } from "./screens/LibraryPosts";
import { LibraryQuotes } from "./screens/LibraryQuotes";
import { BrandSetup } from "./screens/BrandSetup";
import { Onboard } from "./screens/Onboard";
import { Login } from "./screens/Login";
import { NewJob, type SourceMode } from "./screens/NewJob";
import { Operator } from "./screens/Operator";
import { Queue } from "./screens/Queue";
import { Requests } from "./screens/Requests";
import { Settings } from "./screens/Settings";
import type { SectionId } from "./lib/settingsSections";
import { Sources } from "./screens/Sources";
import { StudioEditor } from "./screens/StudioEditor";
import { TourProvider } from "./components/tour/TourProvider";
import { getJob, getSettings, isRenderedScene, isScannedScene, listBacklog, listBetaPostDrafts, listChannelsApi, listJobs, type BacklogDraft, type BetaPostDraft, type ChannelFeatures, type JobListItem, type SettingsInfo } from "./api";
import { forgetJobTitle, list as listTrackedJobs, readJobTitle, terminalKind, track as trackJob, untrack as untrackJob, type StorageLike } from "./lib/trackedJobs";
import type { TourRoute, TourStep } from "./lib/tour/steps";
import { TOUR_SHEET_PAD } from "./lib/tour/placement";
import { saveTourState, startTour } from "./lib/tour/state";
import { pickTourVideoId } from "./lib/tour/video";
import { AUTH_EXPIRED_EVENT, checkAuth } from "./auth";

/** Inspiration + Quotes merged behind one nav entry. Quotes tab only
 *  appears for channels with the posts pipeline enabled (quotes derive
 *  from post drafts). */
function InspirationHub({ initialTab, showQuotes }: { initialTab: "inspiration" | "quotes"; showQuotes: boolean }): React.JSX.Element {
  const [tab, setTab] = useState<"inspiration" | "quotes">(showQuotes ? initialTab : "inspiration");
  return (
    <div>
      {showQuotes && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {(["inspiration", "quotes"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "7px 16px", borderRadius: 999, fontSize: 13, fontFamily: "inherit", cursor: "pointer",
              border: "1px solid " + (tab === t ? "rgba(255,213,0,.4)" : "rgba(255,255,255,.08)"),
              background: tab === t ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
              color: tab === t ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
            }}>{t === "inspiration" ? "Inspiration" : "Quotes"}</button>
          ))}
        </div>
      )}
      {showQuotes && tab === "quotes" ? <LibraryQuotes /> : <Inspiration />}
    </div>
  );
}

export type Route =
  | "new"
  | "backlog"
  | "queue"
  | "requests"
  | "library-videos"
  | "library-posts"
  | "library-quotes"
  | "originals"
  | "inspiration"
  | "sources"
  | "calendar"
  | "cost"
  | "settings"
  | "operator"
  | "guide"
  | "onboard"
  | "studio";

const ROUTES: readonly Route[] = [
  "new",
  "backlog",
  "queue",
  "requests",
  "library-videos",
  "library-posts",
  "library-quotes",
  "originals",
  "inspiration",
  "sources",
  "calendar",
  "cost",
  "settings",
  "operator",
  "guide",
  "onboard",
  "studio",
];

// Hash format: `#/<route>` or `#/<route>/<openJobId>`; for the Guide the
// second segment is a section id (`#/guide/test-vs-approve`), never a job.
// Refreshing keeps the operator on the page they were on. Unknown /
// malformed hashes fall back to the New Job screen.
function parseHash(hash: string): { route: Route; openJobId: string | null; guideSection: string | null } {
  const raw = hash.replace(/^#\/?/, "");
  if (!raw) return { route: "new", openJobId: null, guideSection: null };
  const [maybeRoute, maybeSecond, ...rest] = raw.split("/");
  const route = (ROUTES as readonly string[]).includes(maybeRoute) ? (maybeRoute as Route) : "new";
  const second = maybeSecond && rest.length === 0 ? decodeURIComponent(maybeSecond) : null;
  if (route === "guide") return { route, openJobId: null, guideSection: second };
  return { route, openJobId: second, guideSection: null };
}

function formatHash(route: Route, openJobId: string | null, guideSection: string | null): string {
  if (route === "guide") return guideSection ? `#/guide/${encodeURIComponent(guideSection)}` : "#/guide";
  return openJobId ? `#/${route}/${encodeURIComponent(openJobId)}` : `#/${route}`;
}

type AuthGate =
  | { phase: "loading" }
  | { phase: "login" }
  | { phase: "ready" };

/** How often the tracked-jobs watcher asks the server how a render is doing.
 *  Slow on purpose — a render takes around ten minutes, and the job screen has its
 *  own SSE for anyone actually watching. */
const TRACKED_POLL_MS = 15_000;

/** Consecutive failed polls before an id is dropped from the watch list. A
 *  deleted job 404s forever (`{ error: "not found" }`, so the status code is
 *  not in the message) and would otherwise be polled until the tab closes;
 *  three strikes tolerates a redeploy or a flaky minute first. */
const TRACKED_MAX_MISSES = 3;

/** Stand-in for a `localStorage` the browser refuses to hand over — Safari
 *  private mode and blocked site data can throw on the PROPERTY access
 *  itself, before any guard inside trackedJobs can catch it. */
const NULL_STORAGE: StorageLike = { getItem: () => null, setItem: () => {}, removeItem: () => {} };

function jobStore(): StorageLike {
  try {
    return window.localStorage;
  } catch {
    return NULL_STORAGE;
  }
}

/** Read a sessionStorage key without ever throwing (private mode). */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function clearSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* private mode — nothing was stored, nothing to clear */
  }
}

export function App(): React.JSX.Element {
  const [auth, setAuth] = useState<AuthGate>({ phase: "loading" });
  const initial = parseHash(typeof window === "undefined" ? "" : window.location.hash);
  const [route, setRoute] = useState<Route>(initial.route);
  /** Which Settings section to open on, when something deep-linked us there. */
  const [settingsSection, setSettingsSection] = useState<SectionId | null>(null);
  const [openJobId, setOpenJobId] = useState<string | null>(initial.openJobId);
  /** Section id from the hash when the route is the Guide (`#/guide/<id>`). */
  const [guideSection, setGuideSection] = useState<string | null>(initial.guideSection);
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  // Beta post drafts are polled alongside jobs so the sidebar live
  // counter reflects in-flight slide renders (the post pipeline)
  // and not only catalog video jobs.
  const [betaDrafts, setBetaDrafts] = useState<BetaPostDraft[]>([]);
  // Scanned scenes still waiting on the shelf (scan/locate candidates for the
  // active channel) — drives the Backlog nav count badge. Polled with jobs.
  const [backlogSceneCount, setBacklogSceneCount] = useState(0);
  // When the operator hits "Use in New Job" on a backlog row, we route
  // to the New Job screen with this draft pre-filled. Cleared once the
  // form mounts and consumes it (NewJob calls setNewJobPrefill(null)).
  const [newJobPrefill, setNewJobPrefill] = useState<BacklogDraft | null>(null);
  // Scan in flight after the operator hits Auto-scan — handed to the Backlog so it
  // can stream live progress. Cleared once the scan settles (done/failed).
  const [activeScanId, setActiveScanId] = useState<string | null>(null);
  // Re-edit inputs for a failed "find a scene" locate — pre-fills New Job's locate mode.
  const [locatePrefill, setLocatePrefill] = useState<{ url: string; openingLine: string; closingLine: string } | null>(null);
  // Bumped after onboarding completes so the ChannelSwitcher re-lists.
  const [channelsRefresh, setChannelsRefresh] = useState(0);
  // When set, the onboarding wizard opens in EDIT mode for this channel slug.
  const [editSlug, setEditSlug] = useState<string | undefined>(undefined);
  // Active channel's enabled pipelines — gates nav + screens.
  const [features, setFeatures] = useState<ChannelFeatures>({ videos: true, posts: true, originals: true, autoScan: false });
  // How many brands exist (channels mode) — the tour's welcome card is only
  // offered once there is at least one; null until the list has loaded.
  const [channelCount, setChannelCount] = useState<number | null>(null);
  // Active channel's caption language display name — threads into New Job copy
  // so a French channel reads "French captions", not "Arabic". Absent language
  // field on a channel (or channels disabled) → "Arabic" (byte-identical V1).
  const [captionLanguage, setCaptionLanguage] = useState("Arabic");
  // Server feature flag (CHANNELS_ENABLED). When false the entire channel
  // UI is hidden and the app renders the original single-brand V1 layout.
  const [channelsEnabled, setChannelsEnabled] = useState(false);
  // QL_BRAND_STUDY (server: GET /api/settings.features.brandStudy). On → the
  // create action opens the new Brand Setup; off → the classic wizard is the
  // new-channel entry again. An older server (undefined) reads as ON, which is
  // the flag's own default.
  const [brandSetupOn, setBrandSetupOn] = useState(true);
  // Whole /api/settings features object — brandSetupOn above reads one field
  // off it; the Operator route guard (QL_OPERATOR_PANEL) reads another.
  const [settingsFeatures, setSettingsFeatures] = useState<SettingsInfo["features"]>(undefined);
  // ---- "try a video" handoff + render-ready toast ----------------------
  //
  // Brand Setup's Done stashes `ql-first-video-intent` ("sample" or
  // "upload") and reloads. Reading it in the INITIALIZER (a pure read — the
  // key is consumed in an effect below) is what lets the New Job screen
  // paint directly on the right front door; consuming it in an effect would
  // flash one frame of the YouTube form at the first thing a new user ever
  // sees.
  const [firstVideoIntent, setFirstVideoIntent] = useState<SourceMode | null>(() => {
    const raw = readSession("ql-first-video-intent");
    return raw === "sample" || raw === "upload" ? raw : null;
  });
  // The job that IS the user's first video — survives a refresh so the
  // welcome banner is still there when they come back to watch it.
  const [firstVideoId, setFirstVideoId] = useState<string | null>(() => readSession("ql-first-video"));
  // Explicit steering of New Job's front door ("Try another sample" on a
  // failed first video). Cleared when the operator navigates away, so #/new
  // later opens on its own default again.
  const [newJobMode, setNewJobMode] = useState<SourceMode | undefined>(undefined);
  // Jobs this browser started, newest first (localStorage-backed).
  const [trackedIds, setTrackedIds] = useState<string[]>(() => listTrackedJobs(jobStore()));
  const [toasts, setToasts] = useState<RenderToast[]>([]);
  // Consecutive poll failures per tracked id. A ref, not state: it must
  // survive the effect re-running without re-triggering it.
  const missesRef = useRef<Map<string, number>>(new Map());
  // True while a poll pass is in flight — keeps a slow pass from overlapping
  // the next tick (see the poll effect).
  const passRunningRef = useRef(false);
  // Mobile shell state — desktop ignores both (rail stays in flow).
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The guided tour's active step (null when it isn't running). Phones keep
  // the sidebar in a closed drawer, so a `nav-*` step would spotlight nothing:
  // the tour HOLDS the drawer open for those steps (operator, 2026-09-17).
  // Derived, not an effect — it opens with the step and closes with it, and
  // the route-change close below can't race it. Desktop never reads it.
  const [tourStep, setTourStep] = useState<TourStep | null>(null);
  const tourHoldsDrawer = isMobile && tourStep !== null && tourStep.anchor.startsWith("nav-");
  const drawerVisible = drawerOpen || tourHoldsDrawer;

  // ---- auth gate -------------------------------------------------------

  const verify = useCallback(async () => {
    try {
      const s = await checkAuth();
      setChannelsEnabled(!!s.channelsEnabled);
      setAuth({ phase: s.authenticated ? "ready" : "login" });
    } catch {
      // Network failure: treat as locked-out so we don't expose the app
      // shell when we can't confirm a session.
      setAuth({ phase: "login" });
    }
  }, []);

  useEffect(() => { void verify(); }, [verify]);

  useEffect(() => {
    const onExpired = (): void => setAuth({ phase: "login" });
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // ---- jobs polling — only runs once authenticated ---------------------

  const refreshJobs = useCallback(async () => {
    try {
      const items = await listJobs();
      setJobs(items);
    } catch (err) {
      console.warn("listJobs failed", err);
    }
  }, []);

  const refreshBetaDrafts = useCallback(async () => {
    try {
      const items = await listBetaPostDrafts();
      setBetaDrafts(items);
    } catch (err) {
      console.warn("listBetaPostDrafts failed", err);
    }
  }, []);

  const refreshBacklog = useCallback(async () => {
    try {
      const { items } = await listBacklog();
      // The nav badge tracks scenes still WAITING to render (un-rendered,
      // un-archived — the Backlog's "Fresh" chip), so its "N waiting to
      // render" title stays truthful. Rendered and archived scenes remain
      // visible on the Backlog under their own filter chips but don't
      // inflate this count.
      setBacklogSceneCount(items.filter((e) => isScannedScene(e) && !isRenderedScene(e) && !e.archived).length);
    } catch (err) {
      console.warn("listBacklog failed", err);
    }
  }, []);

  useEffect(() => {
    if (auth.phase !== "ready") return;
    void refreshJobs();
    void refreshBetaDrafts();
    void refreshBacklog();
    const id = window.setInterval(() => {
      void refreshJobs();
      void refreshBetaDrafts();
      void refreshBacklog();
    }, 4000);
    return () => window.clearInterval(id);
  }, [auth.phase, refreshJobs, refreshBetaDrafts, refreshBacklog]);

  useEffect(() => {
    document.body.classList.add("ql-bg-orbs");
    return () => document.body.classList.remove("ql-bg-orbs");
  }, []);

  // The handoff key is one-shot: read once into state above, then consumed
  // here so a later reload doesn't re-open the sample picker.
  useEffect(() => {
    if (firstVideoIntent) clearSession("ql-first-video-intent");
  }, [firstVideoIntent]);

  // "Try another sample" steers only the NEXT visit to New Job.
  useEffect(() => {
    if (route !== "new" && newJobMode) setNewJobMode(undefined);
  }, [route, newJobMode]);

  // ---- render-ready toast ----------------------------------------------
  //
  // A render runs for around ten minutes and nobody watches the job screen for that
  // long, so the finish has to come find them. ONE interval covers the whole
  // tracked list and is torn down the moment the list empties; a hidden tab
  // is skipped entirely (nobody can see a toast there, and a backgrounded tab
  // must not keep a poll loop warm — the next visible tick catches up).
  useEffect(() => {
    if (auth.phase !== "ready" || trackedIds.length === 0) return;
    let stopped = false;

    const poll = async (): Promise<void> => {
      if (document.visibilityState === "hidden") return;
      // A pass over 20 ids on a slow link can outlive the 15 s tick. Two
      // overlapping passes would each count a miss for the same unreachable
      // id, so a live job could be dropped in ~2 intervals instead of 3
      // honest strikes. One pass at a time; a skipped tick costs 15 s.
      if (passRunningRef.current) return;
      passRunningRef.current = true;
      try {
        const done: RenderToast[] = [];
        const drop: string[] = [];
        // Settle the whole pass, THEN apply once: untracking mid-loop would
        // re-run this effect and abandon the ids we hadn't reached yet.
        for (const id of trackedIds) {
          if (stopped) return;
          try {
            const job = await getJob(id);
            missesRef.current.delete(id);
            const kind = terminalKind(job.status);
            if (!kind) continue;
            // The display title first (never a UUID upload filename — spec
            // §2.5), then whatever the job carries, then what the screen that
            // started it remembered, and only then the slug (QA D-6).
            const title = job.title?.trim() || job.sourceTitle?.trim() || readJobTitle(id) || job.id;
            forgetJobTitle(id);
            drop.push(id);
            done.push(
              kind === "ready"
                ? { jobId: id, kind, title }
                // "incomplete" lands here too: no video, nothing coming, and
                // errorMsg is usually empty — the toast still names the job
                // so the user can open it.
                : { jobId: id, kind, title, message: job.errorMsg },
            );
          } catch {
            const misses = (missesRef.current.get(id) ?? 0) + 1;
            missesRef.current.set(id, misses);
            // Gone for good (deleted / wiped) — stop watching, no toast: the
            // user didn't lose anything they can act on.
            if (misses >= TRACKED_MAX_MISSES) drop.push(id);
          }
        }
        // Torn down mid-pass (auth dropped, the list changed under us): the
        // verdicts belong to a list that no longer exists, so apply nothing.
        if (stopped || drop.length === 0) return;
        let next = trackedIds;
        for (const id of drop) {
          next = untrackJob(jobStore(), id);
          missesRef.current.delete(id);
        }
        setTrackedIds(next);
        if (done.length > 0) {
          setToasts((prev) => [
            ...done,
            ...prev.filter((t) => !done.some((d) => d.jobId === t.jobId)),
          ].slice(0, MAX_VISIBLE_TOASTS));
        }
      } finally {
        passRunningRef.current = false;
      }
    };

    const timer = window.setInterval(() => { void poll(); }, TRACKED_POLL_MS);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [auth.phase, trackedIds]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.jobId !== id));
  }, []);

  const openJobFromToast = useCallback((id: string) => {
    setRoute("queue");
    setOpenJobId(id);
  }, []);

  const openStudioFromToast = useCallback((id: string) => {
    setOpenJobId(id);
    setRoute("studio");
  }, []);

  useEffect(() => {
    if (auth.phase !== "ready") return;
    void getSettings()
      .then((s) => {
        setBrandSetupOn(s.features?.brandStudy !== false);
        setSettingsFeatures(s.features);
      })
      .catch(() => { /* older server / blip — keep the default */ });
  }, [auth.phase]);

  // Load the active channel's features (gates nav + redirects off disabled
  // screens). Re-runs when a channel is created/switched.
  useEffect(() => {
    if (auth.phase !== "ready" || !channelsEnabled) return;
    void (async () => {
      try {
        const r = await listChannelsApi();
        setChannelCount(r.channels.length);
        const active = r.channels.find((c) => c.slug === r.activeSlug);
        if (active) {
          setFeatures(active.features);
          // `Channel` (api.ts) doesn't declare `language`, but the server
          // returns it at runtime for non-Arabic channels. Read it narrowly;
          // absent → "Arabic".
          setCaptionLanguage((active as { language?: { displayName?: string } }).language?.displayName ?? "Arabic");
        }
      } catch { /* keep permissive defaults */ }
    })();
  }, [auth.phase, channelsEnabled, channelsRefresh]);

  // Guard: if the active channel disabled the pipeline behind the current
  // route, bounce to Library Videos (always available).
  useEffect(() => {
    if (route === "library-posts" && !features.posts) setRoute("library-videos");
  }, [route, features]);

  // Guard: the Studio route is per-clip only — a bare #/studio (no job id)
  // has nothing to mount, so bounce to the Library.
  useEffect(() => {
    if (route === "studio" && !openJobId) setRoute("library-videos");
  }, [route, openJobId]);

  // Persist route + openJobId to the URL hash so refreshing the page
  // (or sharing the URL) keeps the operator on the same screen instead
  // of bouncing back to "New Job". Uses replaceState so navigation
  // within the SPA doesn't pollute the browser history with one entry
  // per click.
  useEffect(() => {
    const target = formatHash(route, openJobId, guideSection);
    if (window.location.hash !== target) {
      window.history.replaceState(null, "", target);
    }
  }, [route, openJobId, guideSection]);

  // React to browser back/forward (and direct hash edits) so the URL
  // remains the source of truth for the active screen.
  useEffect(() => {
    const onHashChange = (): void => {
      const next = parseHash(window.location.hash);
      setRoute(next.route);
      setOpenJobId(next.openJobId);
      setGuideSection(next.guideSection);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Close the mobile drawer on any navigation; lock body scroll while it's open.
  useEffect(() => { setDrawerOpen(false); }, [route, openJobId]);
  useEffect(() => {
    if (!drawerVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerVisible]);

  if (auth.phase === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ql-fg-muted)",
          fontSize: 13,
        }}
      >
        Loading…
      </div>
    );
  }

  if (auth.phase === "login") {
    return <Login onSuccess={() => setAuth({ phase: "ready" })} />;
  }

  // Brand Setup renders in FOCUS MODE — outside the app shell entirely, same
  // as the `Login` early return just above: no sidebar, no header (spec
  // §2.1). It must come after every hook above so hook order never changes
  // across renders, and before the shell return below so the wizard never
  // gets the sidebar squeezing its width.
  if (route === "onboard" && !editSlug && brandSetupOn) {
    return (
      <BrandSetup
        onDone={(r: { slug: string; tryVideo: boolean; intent?: "sample" | "upload" | "tour" | false }) => {
          // The channel exists and is active. Reload for the same reason
          // ChannelSwitcher does after an activate: the active channel
          // changes server-side state almost every screen reads. The
          // intent is stashed FIRST so New Job can open on the right front
          // door once the app comes back up. `intent` may be absent on an
          // older BrandSetup build — fall back to the `tryVideo` boolean it
          // always carries.
          const intent = r.intent ?? (r.tryVideo ? "sample" : false);
          if (intent === "sample" || intent === "upload") {
            try { sessionStorage.setItem("ql-first-video-intent", intent); } catch { /* private mode */ }
          }
          // "Show me around": the tour is persisted as active BEFORE the
          // reload, so the provider picks it up on step 1 — which lives on
          // New job, where a truthy intent already lands us.
          if (intent === "tour") saveTourState(jobStore(), startTour());
          setEditSlug(undefined);
          setChannelsRefresh((n) => n + 1);
          setOpenJobId(null);
          setRoute(intent ? "new" : "library-videos");
          window.location.hash = intent ? "#/new" : "#/library-videos";
          window.location.reload();
        }}
        onCancel={() => {
          // Exit keeps the draft (spec §2.6) — just navigate away, same as
          // handleNav("library-videos") below (not yet declared at this
          // point in the render, so inlined).
          setSettingsSection(null);
          setRoute("library-videos");
          setOpenJobId(null);
        }}
      />
    );
  }

  // Only count actually in-flight jobs (not orphan "incomplete" or finished/failed).
  const activeJobCount = jobs.filter(
    (j) => j.status !== "ready" && j.status !== "failed" && j.status !== "incomplete",
  ).length;
  // Beta posts are "in-flight" when either Claude is writing the
  // hooks (generating-text) or Puppeteer is composing slides
  // (rendering). Both are transient states that should drive the
  // sidebar live counter and the Library Posts chip.
  const activeBetaPostCount = betaDrafts.filter(
    (d) => d.status === "rendering" || d.status === "generating-text",
  ).length;
  const activeLiveCount = activeJobCount + activeBetaPostCount;
  // A scan or locate is streaming → pulse the Backlog nav badge so the
  // operator can see work landing on the shelf from any screen.
  const backlogScanning = activeScanId !== null;

  const handleNav = (r: Route): void => {
    if (r !== "settings") setSettingsSection(null);
    setRoute(r);
    setOpenJobId(null);
    setGuideSection(null);
  };

  const handleOpenJob = (id: string): void => setOpenJobId(id);
  const handleBack = (): void => setOpenJobId(null);

  const handleCreated = (id: string): void => {
    // EVERY job started from this screen is watched, not just the first one —
    // the toast is what makes walking away from a 10-minute render safe.
    setTrackedIds(trackJob(jobStore(), id));
    if (firstVideoIntent) {
      try { sessionStorage.setItem("ql-first-video", id); } catch { /* private mode */ }
      setFirstVideoId(id);
      setFirstVideoIntent(null);
    }
    void refreshJobs();
    setRoute("queue");
    setOpenJobId(id);
  };

  // ---- guided tour bridge ----------------------------------------------
  //
  // The tour speaks in its own five routes; these map them onto the app's
  // state. "video" is the video page (a job open outside the Studio),
  // "studio" the Studio for that job. The polled `jobs` list is the same
  // list /api/library filters, so "do we have a video" needs no fetch.
  const tourVideoId = pickTourVideoId(jobs, firstVideoId);
  const tourRoute: TourRoute | null = openJobId
    ? (route === "studio" ? "studio" : "video")
    : route === "new" || route === "queue" || route === "library-videos"
      ? route
      : null;
  const tourNavigate = (target: TourRoute): void => {
    if (target === "new" || target === "queue" || target === "library-videos") {
      handleNav(target);
      return;
    }
    if (!tourVideoId) {
      handleNav("library-videos");
      return;
    }
    setSettingsSection(null);
    setOpenJobId(tourVideoId);
    setRoute(target === "studio" ? "studio" : "library-videos");
  };
  // Offer the welcome once there is a brand to tour, never on top of the
  // classic wizard, and never on top of the first-video handoff the user
  // just chose on Brand Setup's Done screen — neither while they pick
  // (`firstVideoIntent`) nor once they clicked Start and are watching it
  // build (`firstVideoId`, which is what that click flips the intent into).
  const offerWelcome =
    (channelsEnabled ? (channelCount ?? 0) > 0 : true) && route !== "onboard" && firstVideoIntent === null && firstVideoId === null;

  // One element for both places Settings renders (its own route, and the
  // #/operator fallback on a deployment without the operator panel).
  // Posting is per brand; a single-brand deployment always has posts.
  const settingsScreen = (
    <Settings
      channelsEnabled={channelsEnabled}
      postsEnabled={channelsEnabled ? features.posts : true}
      operatorPanel={settingsFeatures?.operatorPanel !== false}
      onEditBrand={(slug) => { setEditSlug(slug); setRoute("onboard"); }}
      {...(settingsSection ? { initialSection: settingsSection } : {})}
    />
  );

  return (
    <TourProvider navigate={tourNavigate} hasVideo={tourVideoId !== null} currentRoute={tourRoute} offerWelcome={offerWelcome} onStep={setTourStep}>
    <div style={{ display: isMobile ? "block" : "flex", minHeight: "100vh" }}>
      {isMobile ? (
        <>
          {/* Mobile top bar: hamburger + logo + channel switcher */}
          <header
            style={{
              position: "sticky",
              top: 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              background: "rgba(8,9,14,.92)",
              borderBottom: "1px solid rgba(255,255,255,.06)",
              backdropFilter: "blur(12px)",
            }}
          >
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.08)",
                background: "rgba(255,255,255,.03)",
                color: "var(--ql-fg)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Menu size={20} strokeWidth={2} />
            </button>
            <img
              src="/logo-quotologies.png"
              alt="Quotologies"
              style={{ height: 26, width: "auto", display: "block" }}
            />
            {channelsEnabled && (
              <div style={{ marginLeft: "auto", minWidth: 0 }}>
                <ChannelSwitcher
                  refreshKey={channelsRefresh}
                  onNavigateOnboard={() => { setEditSlug(undefined); setRoute("onboard"); }}
                  onEditChannel={(slug) => { setEditSlug(slug); setRoute("onboard"); }}
                />
              </div>
            )}
          </header>
          {/* Off-canvas nav drawer + scrim */}
          {drawerVisible && (
            <div
              onClick={() => setDrawerOpen(false)}
              className="ql-scrim-in"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 50,
                background: "rgba(0,0,0,.55)",
                backdropFilter: "blur(2px)",
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="ql-drawer-in"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: "min(284px, 86vw)",
                  overflowY: "auto",
                  boxShadow: "2px 0 24px rgba(0,0,0,.5)",
                }}
              >
                <Sidebar
                  active={route}
                  channelsEnabled={channelsEnabled}
                  features={features}
                  activeJobCount={activeJobCount}
                  activeBetaPostCount={activeBetaPostCount}
                  activeLiveCount={activeLiveCount}
                  backlogSceneCount={backlogSceneCount}
                  backlogScanning={backlogScanning}
                  onNav={(r) => { handleNav(r); setDrawerOpen(false); }}
                  onNavigateOnboard={() => { setEditSlug(undefined); setRoute("onboard"); setDrawerOpen(false); }}
                  variant="drawer"
                  onClose={() => setDrawerOpen(false)}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <Sidebar
          active={route}
          channelsEnabled={channelsEnabled}
          features={features}
          activeJobCount={activeJobCount}
          activeBetaPostCount={activeBetaPostCount}
          activeLiveCount={activeLiveCount}
          backlogSceneCount={backlogSceneCount}
          backlogScanning={backlogScanning}
          onNav={handleNav}
        />
      )}
      <main
        style={
          isMobile
            // The tour's bottom sheet publishes its height while it is up
            // (0px otherwise), so the last control on a page can be scrolled
            // above the sheet instead of sitting under it.
            ? { width: "100%", minWidth: 0, padding: `16px 16px calc(48px + ${TOUR_SHEET_PAD})` }
            : {
                flex: 1,
                minWidth: 0,
                padding: "32px 40px 60px",
                maxWidth: 1400,
                margin: "0 auto",
                width: "100%",
              }
        }
        className="ql-fade-up"
        key={openJobId ?? route}
      >
        {!isMobile && channelsEnabled && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <ChannelSwitcher
              refreshKey={channelsRefresh}
              onNavigateOnboard={() => { setEditSlug(undefined); setRoute("onboard"); }}
              onEditChannel={(slug) => { setEditSlug(slug); setRoute("onboard"); }}
            />
          </div>
        )}
        {openJobId && route === "studio" ? (
          <StudioEditor
            jobId={openJobId}
            // Back returns to the clip's detail screen (keeps openJobId).
            onBack={() => setRoute("library-videos")}
            // The chat's balance chip → whose key is paying / top up.
            onOpenSettings={() => {
              // Land ON the AI section: "Credit $0.00" is only actionable
              // there, and eleven nav rows is not a way to find it.
              setSettingsSection("ai");
              handleNav("settings");
            }}
          />
        ) : openJobId ? (
          <JobDetail
            jobId={openJobId}
            onBack={handleBack}
            captionLanguage={captionLanguage}
            onOpenStudio={() => setRoute("studio")}
            firstVideo={firstVideoId !== null && firstVideoId === openJobId}
            onTryAnother={() => {
              // The retry is still the FIRST video — the dead one never
              // produced anything. Re-arm the intent so the picker gets its
              // intro line back and the next job created from it inherits
              // the banner, and let go of the job that failed.
              clearSession("ql-first-video");
              setFirstVideoId(null);
              setFirstVideoIntent("sample");
              setOpenJobId(null);
              setNewJobMode("sample");
              setRoute("new");
            }}
          />
        ) : route === "new" ? (
          <NewJob
            onCreated={handleCreated}
            initialMode={newJobMode ?? (firstVideoIntent ?? undefined)}
            firstVideo={firstVideoIntent !== null}
            prefill={newJobPrefill}
            onConsumePrefill={() => setNewJobPrefill(null)}
            locatePrefill={locatePrefill}
            onConsumeLocatePrefill={() => setLocatePrefill(null)}
            features={features}
            captionLanguage={captionLanguage}
            onSavedToBacklog={() => {
              // After the success-pulse plays for ~2 s, drop the
              // operator onto the Backlog so they can SEE their saved
              // draft in context. Pulse-then-navigate matches how
              // most form apps confirm async writes.
              window.setTimeout(() => setRoute("backlog"), 1800);
            }}
            onScanComplete={(scanId) => {
              setActiveScanId(scanId ?? null);
              setRoute("backlog");
            }}
          />
        ) : route === "backlog" ? (
          <Backlog
            channelsEnabled={channelsEnabled}
            activeScanId={activeScanId}
            onScanSettled={() => { setActiveScanId(null); void refreshBacklog(); }}
            onEditScan={(scan) => {
              setLocatePrefill({
                url: scan.url ?? "",
                openingLine: scan.openingLine ?? "",
                closingLine: scan.closingLine ?? "",
              });
              setActiveScanId(null);
              setRoute("new");
            }}
            onUseInNewJob={(draft) => {
              setNewJobPrefill(draft);
              setRoute("new");
            }}
            onNavigateQueue={(id) => {
              void refreshJobs();
              setRoute("queue");
              setOpenJobId(id);
            }}
            onNavigateRequests={() => setRoute("requests")}
          />
        ) : route === "queue" ? (
          <Queue jobs={jobs} onOpen={handleOpenJob} onChange={refreshJobs} />
        ) : route === "requests" ? (
          <Requests onOpenBacklog={() => setRoute("backlog")} onOpenJob={handleOpenJob} />
        ) : route === "library-videos" ? (
          <Library
            onOpen={handleOpenJob}
            onOpenStudio={(id) => { setOpenJobId(id); setRoute("studio"); }}
          />
        ) : route === "library-posts" ? (
          <LibraryPosts />
        ) : route === "library-quotes" ? (
          channelsEnabled ? <InspirationHub initialTab="quotes" showQuotes={features.posts} /> : <LibraryQuotes />
        ) : route === "originals" ? (
          <div style={{ color: "var(--ql-fg-muted)", fontSize: 14, padding: 40 }}>Originals — coming soon.</div>
        ) : route === "inspiration" ? (
          channelsEnabled ? <InspirationHub initialTab="inspiration" showQuotes={features.posts} /> : <Inspiration />
        ) : route === "sources" ? (
          <Sources
            onUseInNewJob={(draft) => {
              setNewJobPrefill(draft);
              setRoute("new");
            }}
          />
        ) : route === "calendar" ? (
          <Calendar />
        ) : route === "cost" ? (
          <Cost />
        ) : route === "onboard" ? (
          <Onboard
            editSlug={editSlug}
            onDone={() => {
              // Reload so server-side active-channel state propagates
              // everywhere (settings endpoint, library, scheduling).
              // Hash already updates via the route effect below.
              setEditSlug(undefined);
              setChannelsRefresh((n) => n + 1);
              window.location.reload();
            }}
          />
        ) : route === "operator" ? (
          settingsFeatures?.operatorPanel === false ? settingsScreen : <Operator channelsEnabled={channelsEnabled} />
        ) : route === "guide" ? (
          <Guide section={guideSection} onSectionChange={setGuideSection} />
        ) : (
          settingsScreen
        )}
      </main>
      <RenderReadyToast
        toasts={toasts}
        onOpenStudio={openStudioFromToast}
        onOpenJob={openJobFromToast}
        onDismiss={dismissToast}
      />
    </div>
    </TourProvider>
  );
}
